import { IconPlusCircle } from "@/assets/icons/geist/IconPlusCircle";
import { Button } from "@/components/flexkit/Button";
import { Checkbox } from "@/components/flexkit/checkbox/Checkbox";
import { cn } from "@/components/podkit/lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/podkit/popover/Popover";
import { Text } from "@/components/podkit/typography/Text";
import type { PlainDomainVerification } from "@/queries/organization-queries";
import { DomainVerificationState } from "gitpod-next-api/gitpod/v1/organization_pb";
import { ChevronDown } from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";

type DomainSelectorProps = {
    /** All domain verifications in the organization */
    domainVerifications: PlainDomainVerification[];
    /** Domains already assigned to this SSO config */
    assignedDomains: string[];
    /** Called when a domain is selected to be added */
    onAddDomains: (domains: string[]) => void;
    /** Called when a domain is removed */
    onRemoveDomain?: (domain: string) => void;
    /** Called when user wants to create a new domain */
    onNewDomain: () => void;
    /** Whether the selector is disabled */
    disabled?: boolean;
    className?: string;
};

export const DomainSelector: FC<DomainSelectorProps> = ({
    domainVerifications,
    assignedDomains,
    onAddDomains,
    onRemoveDomain,
    onNewDomain,
    disabled = false,
    className,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const allDomains = useMemo(() => {
        return domainVerifications.filter(
            (v) => v.state === DomainVerificationState.VERIFIED || v.state === DomainVerificationState.PENDING,
        );
    }, [domainVerifications]);

    // Toggle domain assignment immediately on click
    const handleToggleDomain = useCallback(
        (domain: string) => {
            const isAssigned = assignedDomains.includes(domain);
            if (isAssigned) {
                onRemoveDomain?.(domain);
            } else {
                onAddDomains([domain]);
            }
        },
        [assignedDomains, onAddDomains, onRemoveDomain],
    );

    const handleNewDomain = useCallback(() => {
        setIsOpen(false);
        onNewDomain();
    }, [onNewDomain]);

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen} modal={false}>
            <PopoverTrigger asChild>
                <Button
                    variant="secondary"
                    size="sm"
                    disabled={disabled}
                    className={cn("gap-1", className)}
                    data-testid="domain-selector-trigger"
                    data-tracking-id="domain-selector-trigger"
                >
                    Domains
                    <ChevronDown className="size-4" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-0" sideOffset={4}>
                {/* Domain list */}
                <div className="max-h-64 overflow-y-auto">
                    {/* Domains that can be toggled */}
                    {allDomains.map((verification) => {
                        const isAssigned = assignedDomains.includes(verification.domain);
                        return (
                            <div
                                key={verification.id}
                                className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-surface-02"
                                onClick={() => handleToggleDomain(verification.domain)}
                                data-testid={`domain-selector-item-${verification.domain}`}
                                data-tracking-id="domain-selector-item"
                            >
                                <Checkbox
                                    checked={isAssigned}
                                    onCheckedChange={() => handleToggleDomain(verification.domain)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="data-[state=checked]:bg-content-primary"
                                    data-testid={`domain-selector-checkbox-${verification.domain}`}
                                    data-tracking-id="domain-selector-checkbox"
                                />
                                <Text className="flex-1 truncate text-sm">{verification.domain}</Text>
                                {verification.state === DomainVerificationState.PENDING && (
                                    <span className="text-xs text-content-secondary">Pending</span>
                                )}
                            </div>
                        );
                    })}

                    {/* No domains available message */}
                    {allDomains.length === 0 && (
                        <div className="px-3 py-4 text-center text-sm text-content-secondary">No domains available</div>
                    )}
                </div>

                {/* New domain option */}
                <div className="border-t border-border-light">
                    <div
                        className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-surface-02"
                        onClick={handleNewDomain}
                        data-testid="domain-selector-new-domain"
                        data-tracking-id="domain-selector-new-domain"
                    >
                        <IconPlusCircle size="sm" className="size-4 text-content-secondary" />
                        <Text className="text-sm">New email domain</Text>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};
